import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

const QUICK_TAGS = [
  "Vegan",
  "No Raw Fish",
  "Spicy",
  "Vegetarian",
  "New",
  "Bestseller",
  "French Menu",
  "Black Angus",
  "High Protein",
];

const TRANSITION_DURATION = 300;

const MenuFilter = ({
  open,
  onClose,
  selectedTags,
  onTagToggle,
  selectedAllergenIds,
  onAllergenToggle,
  onApplyAllergenFilter,
  allergenList,
  onClearAll,
  themeColors
}) => {
  const [allergenExpanded, setAllergenExpanded] = useState(false);

  const handleApply = useCallback(() => {
    onApplyAllergenFilter();
  }, [onApplyAllergenFilter]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const hasActiveFilters =
    selectedTags.length > 0 || selectedAllergenIds.length > 0;

  return (
    /* Backdrop overlay — scoped to the container via position: absolute */
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 20,
        backgroundColor: open ? "rgba(0, 0, 0, 0.4)" : "rgba(0, 0, 0, 0)",
        visibility: open ? "visible" : "hidden",
        transition: `background-color ${TRANSITION_DURATION}ms ease, visibility ${TRANSITION_DURATION}ms ease`,
      }}
    >
      {/* Sliding panel */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width: "85%",
          maxWidth: "360px",
          backgroundColor: "#ebebeb",
          borderRadius: "12px 0 0 12px",
          boxShadow: open ? "-4px 0 24px rgba(0, 0, 0, 0.15)" : "none",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: `transform ${TRANSITION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2.5,
            py: 2,
            flexShrink: 0,
            backgroundColor: "#ffffff"
          }}
        >
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 700,
              fontSize: "20px",
              color: "#1d3153",
            }}
          >
            Filters
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {hasActiveFilters && (
              <Typography
                onClick={onClearAll}
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 500,
                  fontSize: "13px",
                  color: "#b99c74",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Clear all
              </Typography>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Divider />

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {/* Section 1: Quick Tags */}
          <Typography
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "15px",
              color: "#333",
              mb: 1.5,
            }}
          >
            Quick Tags
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {QUICK_TAGS.map((tag) => {
              const isSelected = selectedTags.includes(tag);
              return (
                <Chip
                  key={tag}
                  label={tag}
                  clickable
                  onClick={() => onTagToggle(tag)}
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 500,
                    fontSize: "13px",
                    borderRadius: "20px",
                    backgroundColor: isSelected ? "#1d3153" : "#fff",
                    color: isSelected ? "#fff" : "#555",
                    border: isSelected
                      ? "1px solid #1d3153"
                      : "1px solid #e0e0e0",
                    "&:hover": {
                      backgroundColor: isSelected ? "#162744" : "#eeeeee",
                    },
                  }}
                />
              );
            })}
          </Box>

          {/* Section 2: Allergen Filter */}
          <Accordion
            expanded={allergenExpanded}
            onChange={(_, expanded) => setAllergenExpanded(expanded)}
            disableGutters
            elevation={0}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px !important",
              "&::before": { display: "none" },
              "&.Mui-expanded": { margin: 0 },
              boxShadow:"0 -4px 4px rgba(0, 0, 0, 0.1)"
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                minHeight: "48px",
                "& .MuiAccordionSummary-content": { margin: "12px 0" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "15px",
                  color: "#333",
                }}
              >
                Filter by Allergens
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontSize: "12px",
                  color: "#888",
                  mb: 2,
                }}
              >
                Removes dishes containing these allergens.
              </Typography>

              {allergenList.length === 0 ? (
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    color: "#aaa",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  No allergens available
                </Typography>
              ) : (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}
                >
                  {allergenList.map((allergen) => {
                    const isSelected = selectedAllergenIds.includes(
                      allergen._id
                    );
                    return (
                      <Chip
                        key={allergen._id}
                        label={allergen.name}
                        clickable
                        onClick={() => onAllergenToggle(allergen._id)}
                        avatar={
                          allergen.icon ? (
                            <img
                              src={allergen.icon}
                              alt={allergen.name}
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                              }}
                            />
                          ) : undefined
                        }
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 500,
                          fontSize: "13px",
                          borderRadius: "20px",
                          backgroundColor: isSelected ? "#d32f2f" : "#fff5f5",
                          color: isSelected ? "#fff" : "#c62828",
                          border: isSelected
                            ? "1px solid #d32f2f"
                            : "1px solid #ffcdd2",
                          "& .MuiChip-avatar": {
                            filter: isSelected ? "brightness(10)" : "none",
                          },
                          "&:hover": {
                            backgroundColor: isSelected
                              ? "#b71c1c"
                              : "#ffebee",
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                onClick={handleApply}
                disabled={selectedAllergenIds.length === 0}
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 600,
                  fontSize: "14px",
                  textTransform: "none",
                  borderRadius: "8px",
                  py: 1.2,
                  backgroundColor: themeColors.menuPopupAddToCartBtnBgColor || "#1d3153",
                  opacity: 0.85,
                  "&:hover": { opacity: 1, background: themeColors.menuPopupAddToCartBtnBgColor || "#1d3153" },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                Apply Allergen Filter
              </Button>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </Box>
  );
};

export default MenuFilter;
